import { useEffect, useRef, useState } from 'react'
import { api } from '../../services/api.js'
import UpgradeInline from '../shared/UpgradeInline.jsx'
import './DocumentsPanel.css'

const DOC_TYPE_LABELS = {
  birth_cert:    'Birth certificate',
  death_cert:    'Death certificate',
  marriage_cert: 'Marriage certificate',
  census:        'Census record',
  military:      'Military record',
  photo:         'Photograph',
  newspaper:     'Newspaper',
  letter:        'Letter',
  other:         'Document',
}

const DOC_TYPE_ICONS = {
  birth_cert: '📄', death_cert: '📄', marriage_cert: '💍',
  census: '📋', military: '🎖️', photo: '🖼️',
  newspaper: '📰', letter: '✉️', other: '📎',
}

const ACCEPTED_EXT = /\.(jpg|jpeg|png|webp|tiff?|pdf)$/i

export default function DocumentsPanel({ personId, canEdit }) {
  const [docs, setDocs] = useState([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [expanded, setExpanded] = useState(null) // docId of expanded card
  const [extracting, setExtracting] = useState(null)
  const [error, setError] = useState(null)
  const [tierError, setTierError] = useState(null)
  const [dragging, setDragging] = useState(false)
  const fileRef = useRef(null)

  useEffect(() => {
    api.get(`/persons/${personId}/documents`)
      .then(setDocs)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [personId])

  async function uploadFiles(files) {
    const list = Array.from(files || []).filter(f => ACCEPTED_EXT.test(f.name))
    if (!list.length) return
    setError(null)
    setTierError(null)
    setUploading(true)
    try {
      for (const file of list) {
        const form = new FormData()
        form.append('file', file)
        form.append('doc_type', guessDocType(file.name))
        const doc = await api.upload(`/persons/${personId}/documents`, form)
        setDocs(prev => [doc, ...prev])
        setExpanded(doc.id)
      }
    } catch (err) {
      // TIER_LIMIT (Free 5/month cap) gets an inline upgrade CTA so
      // the fix is one click away. Anything else remains a regular
      // error string.
      if (err.code === 'TIER_LIMIT') setTierError(err)
      else setError(err.message || 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  async function handleUpload(e) {
    await uploadFiles(e.target.files)
    e.target.value = ''
  }

  // Drag-and-drop handlers on the whole panel so users can drop a file
  // anywhere over it. Only the topmost enter/leave gets counted, preventing
  // flicker when the cursor crosses child elements.
  const dragCount = useRef(0)
  function onDragEnter(e) {
    if (!canEdit) return
    if (!e.dataTransfer?.types?.includes('Files')) return
    e.preventDefault()
    dragCount.current++
    setDragging(true)
  }
  function onDragLeave() {
    dragCount.current = Math.max(0, dragCount.current - 1)
    if (dragCount.current === 0) setDragging(false)
  }
  function onDragOver(e) {
    if (!canEdit) return
    if (!e.dataTransfer?.types?.includes('Files')) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
  }
  function onDrop(e) {
    if (!canEdit) return
    e.preventDefault()
    dragCount.current = 0
    setDragging(false)
    uploadFiles(e.dataTransfer.files)
  }

  async function extract(doc) {
    setError(null)
    setExtracting(doc.id)
    try {
      await api.post(`/persons/${personId}/documents/${doc.id}/extract`, {})
      // Poll until done (max 30s)
      let tries = 0
      while (tries < 15) {
        await sleep(2000)
        const updated = await api.get(`/persons/${personId}/documents/${doc.id}`)
        if (updated.extraction_status === 'done' || updated.extraction_status === 'failed') {
          setDocs(prev => prev.map(d => d.id === doc.id ? updated : d))
          break
        }
        tries++
      }
    } catch (err) {
      setError(err.message || 'Extraction failed')
    } finally {
      setExtracting(null)
    }
  }

  async function refreshDoc(docId) {
    try {
      const updated = await api.get(`/persons/${personId}/documents/${docId}`)
      setDocs(prev => prev.map(d => d.id === docId ? updated : d))
    } catch {}
  }

  async function remove(docId) {
    if (!confirm('Delete this document?')) return
    await api.delete(`/persons/${personId}/documents/${docId}`).catch(() => {})
    setDocs(prev => prev.filter(d => d.id !== docId))
    if (expanded === docId) setExpanded(null)
  }

  if (loading) return null

  return (
    <div
      className={`docs-panel ${dragging ? 'docs-panel--dragging' : ''}`}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <div className="docs-panel__header">
        <span className="docs-panel__title">Documents</span>
        {canEdit && (
          <>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
            >
              {uploading ? 'Uploading…' : '+ Add document'}
            </button>
            <input
              ref={fileRef}
              type="file"
              multiple
              accept=".jpg,.jpeg,.png,.webp,.tiff,.pdf"
              style={{ display: 'none' }}
              onChange={handleUpload}
            />
          </>
        )}
      </div>

      {tierError && (
        <div style={{ margin: '0.5rem 0' }}>
          <UpgradeInline error={tierError} />
        </div>
      )}
      {error && (
        <div className="form-error" style={{ margin: '0.5rem 0' }}>
          {error}
          <button
            className="btn btn-ghost btn-sm"
            style={{ marginLeft: '0.75rem', padding: '0.15rem 0.5rem' }}
            onClick={() => setError(null)}
          >Dismiss</button>
        </div>
      )}

      {docs.length === 0 ? (
        canEdit ? (
          <button
            type="button"
            className="docs-panel__dropzone"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
          >
            <div className="docs-panel__dropzone-icon">📎</div>
            <div className="docs-panel__dropzone-title">
              {uploading ? 'Uploading…' : 'Drop a file here or click to upload'}
            </div>
            <div className="docs-panel__dropzone-hint">
              Birth certificates, marriage records, photos, letters — JPG, PNG, TIFF, PDF
            </div>
          </button>
        ) : (
          <div className="docs-panel__empty">No documents yet.</div>
        )
      ) : (
        <div className="docs-panel__list">
          {docs.map(doc => (
            <DocCard
              key={doc.id}
              doc={doc}
              personId={personId}
              expanded={expanded === doc.id}
              extracting={extracting === doc.id}
              canEdit={canEdit}
              onToggle={() => setExpanded(expanded === doc.id ? null : doc.id)}
              onExtract={() => extract(doc)}
              onDelete={() => remove(doc.id)}
              onEntityChanged={() => refreshDoc(doc.id)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function DocCard({ doc, personId, expanded, extracting, canEdit, onToggle, onExtract, onDelete, onEntityChanged }) {
  const isImage = doc.mime_type?.startsWith('image/')
  const ef = doc.extracted_fields || {}
  const hasParsed = doc.extraction_status === 'done' && ef.summary
  const entities = doc.entities || []

  // Local edit state for the "Your details" section. Seeded from the doc
  // record so reverting = re-rendering.
  const [docDate, setDocDate] = useState(doc.doc_date || '')
  const [docNotes, setDocNotes] = useState(doc.notes || '')
  const [docSource, setDocSource] = useState(doc.source_label || '')
  const [savingMeta, setSavingMeta] = useState(false)
  const [savedAt, setSavedAt] = useState(0)
  useEffect(() => {
    setDocDate(doc.doc_date || '')
    setDocNotes(doc.notes || '')
    setDocSource(doc.source_label || '')
  }, [doc.id, doc.doc_date, doc.notes, doc.source_label])

  const dirty = docDate !== (doc.doc_date || '')
             || docNotes !== (doc.notes || '')
             || docSource !== (doc.source_label || '')

  async function saveMeta() {
    setSavingMeta(true)
    try {
      await api.patch(`/persons/${personId}/documents/${doc.id}`, {
        doc_date: docDate.trim() || null,
        notes: docNotes.trim() || null,
        source_label: docSource.trim() || null,
      })
      setSavedAt(Date.now())
      onEntityChanged?.()
    } catch {
      // silent — the dirty state stays so the user can retry
    } finally {
      setSavingMeta(false)
    }
  }

  return (
    <div className={`doc-card ${expanded ? 'doc-card--open' : ''}`}>
      <div className="doc-card__row" onClick={onToggle}>
        <span className="doc-card__icon">{DOC_TYPE_ICONS[doc.doc_type] || '📎'}</span>
        <div className="doc-card__meta">
          <div className="doc-card__name">{doc.file_name}</div>
          <div className="doc-card__sub">
            {DOC_TYPE_LABELS[doc.doc_type] || 'Document'}
            {doc.source_label && ` · ${doc.source_label}`}
            {doc.doc_date && ` · ${doc.doc_date}`}
          </div>
        </div>
        <div className="doc-card__status">
          {doc.extraction_status === 'done' && <span className="doc-badge doc-badge--done">Analysed</span>}
          {doc.extraction_status === 'processing' && <span className="doc-badge doc-badge--processing">Analysing…</span>}
          {doc.extraction_status === 'failed' && <span className="doc-badge doc-badge--failed">Failed</span>}
        </div>
        <span className="doc-card__chevron">{expanded ? '▾' : '▸'}</span>
      </div>

      {expanded && (
        <div className="doc-card__body">
          {isImage && (
            <div className="doc-card__preview">
              <a href={doc.url} target="_blank" rel="noreferrer">
                <img src={doc.url} alt={doc.file_name} />
              </a>
            </div>
          )}

          {!isImage && (
            <a className="doc-card__pdf-link" href={doc.url} target="_blank" rel="noreferrer">
              Open PDF ↗
            </a>
          )}

          {canEdit && (
            <div className="doc-card__meta-edit">
              <div className="doc-card__section-label">Your details</div>
              <div className="doc-card__meta-grid">
                <label>
                  <span>Document date</span>
                  <input
                    type="text"
                    value={docDate}
                    onChange={e => setDocDate(e.target.value)}
                    placeholder="e.g. 1887 · March 1887 · 1887-03-15"
                  />
                </label>
                <label>
                  <span>Source</span>
                  <input
                    type="text"
                    value={docSource}
                    onChange={e => setDocSource(e.target.value)}
                    placeholder="e.g. WieWasWie, Family archive"
                  />
                </label>
                <label className="doc-card__meta-notes">
                  <span>Note</span>
                  <textarea
                    rows={2}
                    value={docNotes}
                    onChange={e => setDocNotes(e.target.value)}
                    placeholder="What does this document show? Why did you add it?"
                  />
                </label>
              </div>
              <div className="doc-card__meta-actions">
                <button
                  className="btn btn-primary btn-sm"
                  disabled={!dirty || savingMeta}
                  onClick={saveMeta}
                >
                  {savingMeta ? 'Saving…' : 'Save details'}
                </button>
                {savedAt > 0 && !dirty && !savingMeta && (
                  <span className="doc-card__meta-saved">Saved</span>
                )}
              </div>
            </div>
          )}

          {hasParsed && (
            <div className="doc-card__extraction">
              <div className="doc-card__section-label">AI Analysis</div>
              {ef.summary && <p className="doc-card__summary">{ef.summary}</p>}
              {ef.document_date && (
                <div className="doc-card__field"><strong>Document date:</strong> {ef.document_date}</div>
              )}
              {ef.document_place && (
                <div className="doc-card__field"><strong>Place:</strong> {ef.document_place}</div>
              )}
              {ef.language && ef.language !== 'Dutch' && (
                <div className="doc-card__field"><strong>Language:</strong> {ef.language}</div>
              )}
              {entities.length > 0 ? (
                <div className="doc-card__persons">
                  <div className="doc-card__section-label" style={{ marginTop: '0.75rem' }}>Persons mentioned</div>
                  {entities.map(e => (
                    <EntityLinkRow
                      key={e.id}
                      entity={e}
                      doc={doc}
                      personId={personId}
                      canEdit={canEdit}
                      onChanged={onEntityChanged}
                    />
                  ))}
                </div>
              ) : ef.persons?.length > 0 && (
                // Fallback for documents analysed before entities were persisted.
                <div className="doc-card__persons">
                  <div className="doc-card__section-label" style={{ marginTop: '0.75rem' }}>Persons mentioned</div>
                  {ef.persons.map((p, i) => (
                    <div key={i} className="doc-card__person">
                      <span className="doc-card__person-name">{p.name}</span>
                      <span className="doc-card__person-role">{p.role}</span>
                      {p.birth_date && <span className="doc-card__person-detail">b. {p.birth_date}</span>}
                      {p.occupation && <span className="doc-card__person-detail">{p.occupation}</span>}
                    </div>
                  ))}
                </div>
              )}
              {ef.transcription && (
                <details className="doc-card__transcription">
                  <summary>Transcription</summary>
                  <pre>{ef.transcription}</pre>
                </details>
              )}
            </div>
          )}

          <div className="doc-card__actions">
            {canEdit && doc.extraction_status !== 'done' && doc.extraction_status !== 'processing' && (
              <button
                className="btn btn-primary btn-sm"
                onClick={onExtract}
                disabled={extracting}
              >
                {extracting ? 'Analysing…' : 'Analyse with AI'}
              </button>
            )}
            {canEdit && doc.extraction_status === 'done' && (
              <button className="btn btn-ghost btn-sm" onClick={onExtract} disabled={extracting}>
                Re-analyse
              </button>
            )}
            {canEdit && (
              <button
                className="btn btn-ghost btn-sm"
                style={{ color: '#dc2626' }}
                onClick={onDelete}
              >
                Delete
              </button>
            )}
          </div>
          {extracting && <ExtractionProgress />}
        </div>
      )}
    </div>
  )
}

// Roles for which creating a tree_relationship makes sense. Witnesses and
// officiants are only cited, not connected.
const ROLE_TO_RELATIONSHIP_LABEL = {
  parent: 'parent',
  child:  'child',
  spouse: 'spouse',
}

function EntityLinkRow({ entity, doc, personId, canEdit, onChanged }) {
  const [mode, setMode] = useState('idle') // idle | linking | busy | dedup
  const [people, setPeople] = useState(null)
  const [search, setSearch] = useState('')
  const [error, setError] = useState(null)
  const [dupMatches, setDupMatches] = useState([])

  const linked = !!entity.confirmed_person_id
  const linkedName = [entity.confirmed_first_name, entity.confirmed_last_name]
    .filter(Boolean).join(' ') || entity.confirmed_preferred_name
  const relLabel = ROLE_TO_RELATIONSHIP_LABEL[entity.role]

  async function resolve(action, payload = {}) {
    setError(null)
    setMode('busy')
    try {
      await api.post(
        `/persons/${personId}/documents/${doc.id}/entities/${entity.id}/resolve`,
        { action, ...payload }
      )
      onChanged?.()
    } catch (err) {
      // 409 POSSIBLE_DUPLICATE → show inline match list. The backend
      // returns up to 5 existing tree persons matching surname + birth
      // year window. User can link to one or click 'Create anyway'.
      if (err.code === 'POSSIBLE_DUPLICATE' && Array.isArray(err.meta?.matches)) {
        setDupMatches(err.meta.matches)
        setMode('dedup')
      } else {
        setError(err.message || 'Failed')
        setMode('idle')
      }
    }
  }

  async function openPicker() {
    setMode('linking')
    if (!people) {
      try {
        const list = await api.get(`/persons?tree_id=${doc.tree_id}`)
        setPeople(list || [])
      } catch {
        setPeople([])
      }
    }
  }

  const filtered = (people || []).filter(p => {
    if (!search) return true
    const name = `${p.first_name || ''} ${p.last_name || ''}`.toLowerCase()
    return name.includes(search.toLowerCase())
  }).slice(0, 12)

  return (
    <div className="doc-card__entity">
      <div className="doc-card__entity-row">
        <span className="doc-card__person-name">{entity.name_raw}</span>
        {entity.role && <span className="doc-card__person-role">{entity.role}</span>}
        {entity.birth_year_est && <span className="doc-card__person-detail">b. {entity.birth_year_est}</span>}
        {entity.occupation && <span className="doc-card__person-detail">{entity.occupation}</span>}
      </div>
      {linked ? (
        <div className="doc-card__entity-linked">
          ✓ Linked to <strong>{linkedName || 'person in tree'}</strong>
        </div>
      ) : mode === 'dedup' ? (
        <div className="doc-card__entity-dedup" style={{
          background: 'rgba(200,150,40,.08)',
          border: '1px solid rgba(200,150,40,.3)',
          borderRadius: 8,
          padding: '.75rem .9rem',
          marginTop: '.4rem',
        }}>
          <div style={{ fontSize: '.85rem', fontWeight: 600, marginBottom: '.5rem' }}>
            {dupMatches.length} possible match{dupMatches.length === 1 ? '' : 'es'} already in this tree
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.35rem', marginBottom: '.5rem' }}>
            {dupMatches.map(m => (
              <button
                key={m.id}
                className="btn btn-sm btn-primary"
                style={{ justifyContent: 'flex-start', textAlign: 'left' }}
                onClick={() => resolve('link_existing', { target_person_id: m.id, create_relationship: !!relLabel })}
              >
                Link to {[m.first_name, m.last_name].filter(Boolean).join(' ')}
                {(m.birth_date || m.death_date) && (
                  <span style={{ opacity: 0.75, marginLeft: '.5rem', fontSize: '.78rem' }}>
                    ({(m.birth_date || '?').slice(0,10)} – {(m.death_date || '?').slice(0,10)})
                  </span>
                )}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '.4rem' }}>
            <button
              className="btn btn-sm btn-ghost"
              onClick={() => resolve('create_new', { create_relationship: !!relLabel, confirm_create: true })}
            >Create new anyway</button>
            <button
              className="btn btn-sm btn-ghost"
              style={{ color: '#6b7280' }}
              onClick={() => { setDupMatches([]); setMode('idle') }}
            >Cancel</button>
          </div>
        </div>
      ) : canEdit && mode !== 'linking' ? (
        <div className="doc-card__entity-actions">
          <button
            className="btn btn-sm btn-primary"
            disabled={mode === 'busy'}
            onClick={() => resolve('create_new', { create_relationship: !!relLabel })}
          >
            + Add to tree{relLabel ? ` as ${relLabel}` : ''}
          </button>
          <button
            className="btn btn-sm btn-ghost"
            disabled={mode === 'busy'}
            onClick={openPicker}
          >
            Link existing…
          </button>
          <button
            className="btn btn-sm btn-ghost"
            style={{ color: '#6b7280' }}
            disabled={mode === 'busy'}
            onClick={() => resolve('dismiss')}
          >
            Dismiss
          </button>
        </div>
      ) : mode === 'linking' ? (
        <div className="doc-card__entity-picker">
          <input
            type="text"
            className="doc-card__entity-search"
            placeholder="Search persons in this tree…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
          />
          <div className="doc-card__entity-picker-list">
            {people === null ? (
              <div className="doc-card__entity-picker-empty">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="doc-card__entity-picker-empty">No matches</div>
            ) : filtered.map(p => (
              <button
                key={p.id}
                className="doc-card__entity-picker-item"
                onClick={() => resolve('link_existing', {
                  target_person_id: p.id,
                  create_relationship: !!relLabel,
                })}
              >
                <strong>{p.first_name} {p.last_name}</strong>
                {p.birth_date && <span> · b. {p.birth_date}</span>}
              </button>
            ))}
          </div>
          <button
            className="btn btn-sm btn-ghost"
            onClick={() => setMode('idle')}
          >
            Cancel
          </button>
        </div>
      ) : null}
      {error && <div className="doc-card__entity-error">{error}</div>}
    </div>
  )
}

// Indeterminate progress bar + elapsed timer. We don't know how long Claude
// Vision will take per document (typically 8–25s), so a real percentage would
// be dishonest — showing motion and elapsed seconds conveys "working" without
// faking precision.
function ExtractionProgress() {
  const [elapsed, setElapsed] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setElapsed(e => e + 1), 1000)
    return () => clearInterval(id)
  }, [])
  return (
    <div className="doc-card__progress" role="status" aria-live="polite">
      <div className="doc-card__progress-bar">
        <div className="doc-card__progress-bar-inner" />
      </div>
      <div className="doc-card__progress-text">
        Reading the document with AI Vision… {elapsed}s
      </div>
    </div>
  )
}

function guessDocType(filename) {
  const f = filename.toLowerCase()
  if (f.includes('birth') || f.includes('geboorte')) return 'birth_cert'
  if (f.includes('death') || f.includes('overlijden')) return 'death_cert'
  if (f.includes('marriage') || f.includes('huwelijk') || f.includes('trouw')) return 'marriage_cert'
  if (f.includes('census') || f.includes('volkstelling')) return 'census'
  if (f.includes('military') || f.includes('militair')) return 'military'
  if (f.includes('newspaper') || f.includes('krant')) return 'newspaper'
  if (f.match(/\.(jpg|jpeg|png|webp|tiff?)$/)) return 'photo'
  return 'other'
}

function sleep(ms) { return new Promise(r => setTimeout(r, ms)) }
